import { Settings2 } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import type { BridgeCatalog, BridgeLeg, BridgeName } from '@/lib/api/speed';
import { cn } from '@/lib/utils';

import { useBridgeOverride } from '../bridgeOverride';
import type { BridgeOverride } from '../bridgeOverride';
import { bridgeLabel, bridgeOptionsFor } from '../services/bridgeFees';

/** What pinning each bridge means for the tester; the order comes from the backend. */
const PIN_HINTS: Record<BridgeName, string> = {
    relay: 'Relay only, no fallback.',
    lifi: 'LI.FI only, no fallback. Routes through the fastest third-party bridge LI.FI offers for the chain.',
    debridge:
        'deBridge only, no fallback. Fixed native fee, $10 minimum on withdrawals.',
};

export interface BridgeOverrideButtonProps {
    disabled?: boolean;
    /**
     * The backend's bridge registry (`useBridgeCatalog`), fetched by the panel
     * container. Null while unknown: every bridge the app knows is offered then.
     */
    catalog?: BridgeCatalog | null;
    /** The chain the bridge leg leaves from (the anchor on a deposit, HyperEVM on a withdrawal). */
    originChainId: number;
    /** The chain it pays out on. */
    destinationChainId: number;
    /** Who signs the leg; decides which bridges can carry it at all. */
    leg: BridgeLeg;
}

/**
 * The small gray gear beside the Cancel and Deposit or Withdraw buttons: pins
 * the bridge for testing. The options are the bridges the backend's registry
 * enables for THIS leg, in the order it tries them, so "Automatic" reads as
 * the real failover chain and a pin can only name a bridge that can run.
 * Shows the pinned bridge's name next to the gear while one is pinned, so a
 * forced route is never mistaken for the automatic one.
 */
const BridgeOverrideButton: React.FC<BridgeOverrideButtonProps> = ({
    disabled,
    catalog = null,
    originChainId,
    destinationChainId,
    leg,
}) => {
    const bridge = useBridgeOverride((s) => s.bridge);
    const setBridge = useBridgeOverride((s) => s.setBridge);
    const pinned = bridge !== 'auto';

    const available = bridgeOptionsFor(
        catalog,
        originChainId,
        destinationChainId,
        leg,
    );
    // a pin the registry does not allow for this leg: still shown, so the tester
    // sees what is pinned and why the backend will refuse it
    const pinnedUnavailable = pinned && !available.includes(bridge);
    const label = (name: BridgeName) => bridgeLabel(catalog, name);

    const options: Array<{
        value: BridgeOverride;
        label: string;
        hint: string;
    }> = [
        {
            value: 'auto',
            label: 'Automatic',
            hint:
                available.length > 0
                    ? `${available.map(label).join(', then ')}: the first one that can serve the route.`
                    : 'No bridge serves this route.',
        },
        ...available.map((name) => ({
            value: name,
            label: label(name),
            hint: PIN_HINTS[name],
        })),
    ];

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    disabled={disabled}
                    aria-label="Bridge settings"
                    title={
                        pinned
                            ? `Bridge pinned to ${label(bridge)}`
                            : 'Bridge settings'
                    }
                    className={cn(
                        'shrink-0 text-muted-foreground/60 hover:text-muted-foreground',
                        pinned && 'text-amber-400 hover:text-amber-300',
                    )}
                >
                    <Settings2 size={14} />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-64 p-2">
                <p className="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    Bridge
                </p>
                <div
                    role="radiogroup"
                    aria-label="Bridge"
                    className="flex flex-col gap-1"
                >
                    {options.map((option) => {
                        const selected = option.value === bridge;
                        return (
                            <button
                                key={option.value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => setBridge(option.value)}
                                className={cn(
                                    'rounded-md px-2 py-1.5 text-left text-xs transition-colors hover:bg-muted',
                                    selected && 'bg-primary/10 text-primary',
                                )}
                            >
                                <span className="block font-medium">
                                    {option.label}
                                </span>
                                <span className="block text-[10px] leading-snug text-muted-foreground">
                                    {option.hint}
                                </span>
                            </button>
                        );
                    })}
                </div>
                {pinned && (
                    <p className="mt-2 px-1 text-[10px] leading-snug text-amber-400">
                        {pinnedUnavailable
                            ? `${label(bridge)} is pinned but cannot carry this transfer, so it will be refused. Pick another bridge or Automatic.`
                            : `Testing mode: every deposit and withdrawal uses ${label(bridge)} until you set this back to Automatic.`}
                    </p>
                )}
            </PopoverContent>
        </Popover>
    );
};

BridgeOverrideButton.displayName = 'BridgeOverrideButton';

export default BridgeOverrideButton;
